import React from 'react';
import { motion } from 'framer-motion';
import { Printer } from 'lucide-react';
import { Monogram } from './Monogram';
import { profileName } from '../data/profile';
import type { Lang, LocaleContent } from '../types/profile';
interface SiteHeaderProps {
  lang: Lang;
  content: LocaleContent;
  onLangChange: (lang: Lang) => void;
}
const languages: {
  code: Lang;
  label: string;
  name: string;
}[] = [{
  code: 'en',
  label: 'EN',
  name: 'English'
}, {
  code: 'th',
  label: 'TH',
  name: 'ภาษาไทย'
}];
export function SiteHeader({
  lang,
  content,
  onLangChange
}: SiteHeaderProps) {
  const links = [{
    href: '#about',
    label: content.nav.about
  }, {
    href: '#projects',
    label: content.nav.projects
  }, {
    href: '#strengths',
    label: content.nav.strengths
  }];
  const linkClass = 'relative py-1 text-[15px] text-ink-soft transition-colors hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/60 rounded-sm after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-sage-deep after:transition-transform after:duration-300 hover:after:scale-x-100';
  return <header className="no-print sticky top-0 z-40 border-b border-line/80 bg-ivory/85 backdrop-blur-md" data-id="element-140" data-mp="2ekpysupvrqli">
      <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-141" data-mp="2oh2s3rhik2fq">
        <div className="flex h-16 items-center justify-between gap-4" data-id="element-142" data-mp="2ogkpmd49crzd">
          <a href="#top" className="group flex items-center gap-3 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/60" aria-label={profileName} data-id="element-143" data-mp="1qu3uneizyqgs">
            <Monogram className="transition-colors duration-300 group-hover:border-sage-deep group-hover:text-sage-deep" data-id="element-144" data-mp="20pxgxpmipybr" />
            <span className="hidden text-[15px] font-medium tracking-tight text-charcoal sm:block" data-id="element-145" data-mp="25kbb5opg1zeu">
              {profileName}
            </span>
          </a>

          <nav aria-label="Primary" className="hidden items-center gap-8 md:flex" data-id="element-146" data-mp="1d0mmsqzob26l">
            {links.map(link => <a key={link.href} href={link.href} className={linkClass} data-id="element-147" data-mp="14zezr1y6sxtt">
                {link.label}
              </a>)}
          </nav>

          <div className="flex items-center gap-2" data-id="element-148" data-mp="1erknfzheot37">
            <div role="group" aria-label={content.actions.languageGroup} className="relative flex items-center rounded-full border border-line bg-paper p-1" data-id="element-149" data-mp="1eq9oqe2tibiy">
              {languages.map(l => {
              const active = l.code === lang;
              return <button key={l.code} type="button" lang={l.code} aria-pressed={active} aria-label={l.name} onClick={() => onLangChange(l.code)} className={`relative z-10 h-8 w-11 rounded-full font-mono text-[13px] font-medium tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/60 ${active ? 'text-ivory' : 'text-ink-soft hover:text-charcoal'}`} data-id="element-150" data-mp="w0jp9do411kp">
                    {active && <motion.span layoutId="lang-pill" className="absolute inset-0 -z-10 rounded-full bg-charcoal" transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 38
                }} data-id="element-151" data-mp="210rsetq68jrh" />}
                    {l.label}
                  </button>;
            })}
            </div>

            <button type="button" onClick={() => window.print()} aria-label={content.actions.print} className="inline-flex h-10 items-center gap-2 rounded-full border border-line px-3 text-[14px] text-charcoal transition-colors hover:border-sage hover:bg-sage-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/60 sm:px-4" data-id="element-152" data-mp="2jxj70purqap7">
              <Printer className="h-4 w-4" strokeWidth={1.6} aria-hidden="true" data-id="element-153" data-mp="1st6tp7i8mk1n" />
              <span className="hidden sm:inline" data-id="element-154" data-mp="1ns125d0bd2tp">{content.actions.print}</span>
            </button>
          </div>
        </div>

        <nav aria-label="Primary" className="-mt-1 flex items-center gap-6 pb-3 md:hidden" data-id="element-155" data-mp="1ivsetcbyq3bg">
          {links.map(link => <a key={link.href} href={link.href} className={linkClass} data-id="element-156" data-mp="2bi3nla9q3ajc">
              {link.label}
            </a>)}
        </nav>
      </div>
    </header>;
}